import { type ReactNode, createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { PageEnum } from "src/utils/tracking/types/pageEnum";

export interface RouteMetadata {
    page?: PageEnum;
    /**
     * Additional metadata can be added as needed, for example to track which marketingAction the user is on
     */
    [key: string]: any;
}

export interface RouteMetadataContextValue {
    metadata: RouteMetadata;
    setMetadata: (metadata: RouteMetadata) => void;
    updateMetadata: (metadata: Partial<RouteMetadata>) => void;
}

const defaultContextValue: RouteMetadataContextValue = {
    metadata: {},
    setMetadata: () => undefined,
    updateMetadata: () => undefined,
};

export const RouteMetadataContext = createContext<RouteMetadataContextValue>(defaultContextValue);

interface RouteMetadataProviderProps {
    children: ReactNode;
    initialMetadata?: RouteMetadata;
}

export function RouteMetadataProvider({
    children,
    initialMetadata = {},
}: RouteMetadataProviderProps): JSX.Element {
    const [metadata, setMetadata] = useState<RouteMetadata>(initialMetadata);

    useEffect(() => {
        setMetadata(initialMetadata);
    }, [initialMetadata]);

    const updateMetadata = useCallback((partial: Partial<RouteMetadata>) => {
        setMetadata((prev) => ({ ...prev, ...partial }));
    }, []);

    const value = useMemo(
        () => ({
            metadata,
            setMetadata,
            updateMetadata,
        }),
        [metadata, updateMetadata]
    );

    return <RouteMetadataContext.Provider value={value}>{children}</RouteMetadataContext.Provider>;
}
